import { useState } from "react";
import { useSite } from "@/lib/site";
import { PageHeader, Reveal } from "@/components/site/common";
import { CtaBand } from "@/components/site/Sections";

export const CaseCard = ({ c }) => (
  <article className="group h-full flex flex-col rounded-3xl bg-white border border-slate-200 overflow-hidden transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_rgba(15,23,42,0.10)]" data-testid={`case-card-${c.id}`}>
    <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
      {c.image && <img src={c.image} alt={c.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
      <span className="absolute top-4 left-4 rounded-full bg-white/90 backdrop-blur px-3 py-1 text-xs font-semibold text-slate-800">{c.category}</span>
      {c.sample && <span className="absolute top-4 right-4 rounded-full bg-[#FF5733] text-white px-3 py-1 text-[11px] font-mono uppercase tracking-widest" data-testid={`case-sample-badge-${c.id}`}>Sample</span>}
    </div>
    <div className="p-6 flex flex-col flex-1">
      <p className="text-xs text-slate-500">{c.client}</p>
      <h3 className="mt-1.5 font-display text-xl font-bold tracking-tight text-slate-900">{c.title}</h3>
      <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{c.summary}</p>
      <div className="mt-5 flex flex-wrap gap-2">
        {c.metrics.map((m) => <span key={m} className="rounded-full bg-blue-50 text-blue-700 px-3 py-1 text-xs font-semibold">{m}</span>)}
      </div>
    </div>
  </article>
);

export default function Work() {
  const { cases } = useSite();
  const cats = ["All", ...new Set(cases.map((c) => c.category).filter(Boolean))];
  const [cat, setCat] = useState("All");
  const shown = cat === "All" ? cases : cases.filter((c) => c.category === cat);
  return (
    <>
      <PageHeader overline="Work / Case studies" title="Work that moves the numbers." testid="work-header">
        A look at the kind of projects we take on — campaigns, launches and websites, each with a clear goal.
      </PageHeader>
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
        <div className="flex flex-wrap gap-2 mb-10" data-testid="work-filters">
          {cats.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`work-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors ${cat === c ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`}>{c}</button>
          ))}
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {shown.map((c, i) => <Reveal key={c.id} delay={i * 0.05}><CaseCard c={c} /></Reveal>)}
        </div>
        {!shown.length && <p className="text-slate-500" data-testid="work-empty">No case studies yet.</p>}
      </section>
      <CtaBand />
    </>
  );
}
